import { __HiringFilterType, __HiringSearchParamsType } from './hiringList.type';
import { nextBrowser } from '@/scripts';
import { PATH } from '@/data';
import {
    HiringExperienceEnum,
    HiringTypeEnum,
    HiringSalaryTypeEnum,
    HiringContractEnum,
    LanguageEnum,
    HiringGenderEnum,
} from '@/types';

export const __JobPositionTypeValue = (i: string) =>
    Number(i) === HiringTypeEnum.remote ? HiringTypeEnum.remote : HiringTypeEnum.office;
export const __JobTimeValue = (i: string) =>
    Number(i) === HiringContractEnum.fulltime ? HiringContractEnum.fulltime : HiringContractEnum.halftime;
export const __JobExperienceValue = (i: string) =>
    Number(i) === HiringExperienceEnum.junior
        ? HiringExperienceEnum.junior
        : Number(i) === HiringExperienceEnum.mid
          ? HiringExperienceEnum.mid
          : HiringExperienceEnum.senior;
export const __HiringGenderValue = (i: string) =>
    Number(i) === HiringGenderEnum.male
        ? HiringGenderEnum.male
        : Number(i) === HiringGenderEnum.female
          ? HiringGenderEnum.female
          : HiringGenderEnum.all;
export const __JobSalaryTypeValue = (i: string) =>
    Number(i) === HiringSalaryTypeEnum.daily
        ? HiringSalaryTypeEnum.daily
        : Number(i) === HiringSalaryTypeEnum.hours
          ? HiringSalaryTypeEnum.hours
          : Number(i) === HiringSalaryTypeEnum.monthly
            ? HiringSalaryTypeEnum.monthly
            : HiringSalaryTypeEnum.project;

export const __HiringListPath = (e: __HiringFilterType, language: LanguageEnum) => {
    const params: { [key: string]: string | string[] } = {};
    if (e.category) params.category = e.category;
    if (e.order) params.order = e.order;
    if (e.search) params.search = e.search;
    if (e.page) params.page = e.page;
    if (e.salaryGTE) params.salaryGTE = e.salaryGTE.toString();
    if (e.salaryLTE) params.salaryLTE = e.salaryLTE.toString();
    if (e.gender) params.gender = e.gender.toString();
    if (e.agreementSalary !== undefined) params.agreementSalary = JSON.stringify(e.agreementSalary);
    if (e.foreign !== undefined) params.foreign = e.foreign ? 'true' : 'false';

    const path = new URL(nextBrowser?.window?.location.origin + PATH(language).hiring.index(params));

    if (typeof e.workType === 'number') path.searchParams.append('workType', e.workType.toString());
    else e.workType?.map((workType) => path.searchParams.append('workType', workType.toString()));
    if (typeof e.time === 'number') path.searchParams.append('time', e.time.toString());
    else e.time?.map((time) => path.searchParams.append('time', time.toString()));
    if (typeof e.experience === 'number') path.searchParams.append('experience', e.experience.toString());
    else e.experience?.map((experience) => path.searchParams.append('experience', experience.toString()));
    if (typeof e.salaryType === 'number') path.searchParams.append('salaryType', e.salaryType.toString());
    else e.salaryType?.map((salaryType) => path.searchParams.append('salaryType', salaryType.toString()));
    return path;
};

export const __InitHiringFilter = (e: __HiringSearchParamsType): __HiringFilterType => ({
    page: e.page ? e.page.toString() : undefined,
    category: e.category ? e.category.toString() : undefined,
    search: e.search ? e.search.toString() : undefined,
    order: e.order ? e.order.toString() : undefined,
    salaryGTE: e.salaryGTE ? Number(e.salaryGTE) : undefined,
    salaryLTE: e.salaryLTE ? Number(e.salaryLTE) : undefined,
    agreementSalary: e.agreementSalary === 'true' ? true : e.agreementSalary === 'false' ? false : undefined,
    foreign: e.foreign === 'true' ? 1 : e.foreign === 'false' ? 0 : undefined,
    gender: Array.isArray(e.gender)
        ? e.gender.map(__HiringGenderValue)
        : e.gender
          ? [__HiringGenderValue(e.gender)]
          : undefined,
    workType: Array.isArray(e.workType)
        ? e.workType.map(__JobPositionTypeValue)
        : e.workType
          ? [__JobPositionTypeValue(e.workType)]
          : undefined,
    time: Array.isArray(e.time) ? e.time.map(__JobTimeValue) : e.time ? [__JobTimeValue(e.time)] : undefined,
    experience: Array.isArray(e.experience)
        ? e.experience.map(__JobExperienceValue)
        : e.experience
          ? [__JobExperienceValue(e.experience)]
          : undefined,
    salaryType: Array.isArray(e.salaryType)
        ? e.salaryType.map(__JobSalaryTypeValue)
        : e.salaryType
          ? [__JobSalaryTypeValue(e.salaryType)]
          : undefined,
});

export const __HiringCheckFilterChange = (filters: __HiringFilterType, initialFilter: __HiringSearchParamsType) => {
    return (
        filters.page === initialFilter.page &&
        filters.order === initialFilter.order &&
        filters.search === initialFilter.search &&
        filters.foreign === initialFilter.foreign &&
        filters.category === initialFilter.category &&
        filters.salaryGTE === initialFilter.salaryGTE &&
        filters.salaryLTE === initialFilter.salaryLTE &&
        filters.agreementSalary === initialFilter.agreementSalary &&
        filters.time?.toString() === initialFilter.time?.toString() &&
        filters.workType?.toString() === initialFilter.workType?.toString() &&
        filters.experience?.toString() === initialFilter.experience?.toString() &&
        filters.salaryType?.toString() === initialFilter.salaryType?.toString()
    );
};

export const __HiringFetchParams = (e: __HiringFilterType, lang: LanguageEnum, brand?: string) => {
    const params: { [key: string]: number | string[] | string | boolean } = {
        page_size: 5,
        is_active: true,
        category__lang: lang,
    };
    if (brand) params.brand__slug__in = brand;
    if (e.page) params.page = e.page;
    if (e.category) params.category_and_descendants = e.category;
    if (e.salaryGTE) params.salary_gte = e.salaryGTE;
    if (e.salaryLTE) params.salary_lte = e.salaryLTE;
    if (e.search) params.title__icontains = e.search;
    if (e.order) params.ordering = e.order;
    if (e.foreign !== undefined) params.foreign = !!e.foreign;
    else params.ordering = '-created_at';
    if (e.workType) params.type__in = e.workType.toString();
    if (e.experience) params.experience__in = e.experience.toString();
    if (e.time) params.contract__in = e.time.toString();
    if (e.salaryType) params.salary_type = e.salaryType.toString();
    if (e.agreementSalary) params.salary_isnull = e.agreementSalary.toString();
    return params;
};
